import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { TFunction, withTranslation } from 'react-i18next';
import Warning from '@material-ui/icons/Warning';
import ArrowForward from '@material-ui/icons/ArrowRightAlt';
import { fontFamily, fontSize } from '../../../../../constants/text';
import { colors } from '../../../../../constants/colors';
import Alert from '../../../../SolvPatternLibrary/Icons/Alert';
import { FadeIn } from '../../../../../core/util/commonCssAnimations';
import { dateHasOpenHours } from '../../../../../core/util/location';
import { getRelativeDayName } from '../../../../../core/util/date';
import { hasSlotsAvailable } from '../../../../../core/location/slots';
import { getClinicClosedXDay, getYouCanStillBookXDay } from '../util';
import Button, { ButtonVariations } from '../../../../SolvPatternLibrary/Button';

const StyledAlert = styled(Alert).withConfig({ componentId: 'sc-18v994t-0' })`
  font-size: 18px;
  padding-top: 1px;
  margin-right: 2px;
`;

const StyledBookingWidgetAlert = styled(Warning).withConfig({ componentId: 'sc-18v994t-1' })`
  color: ${colors.peaches};
  font-size: 17px;
  margin-right: 6px;
`;

const TitleContainer = styled.div.withConfig({ componentId: 'sc-18v994t-2' })`
  display: flex;
  justify-content: center;
  margin: 20px 0 14px 0;
`;

const Title = styled.div.withConfig({ componentId: 'sc-18v994t-3' })`
  color: ${colors.ladyInRed};
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.mediumSmall};
  letter-spacing: -0.27px;
  line-height: 18px;
`;

const StyledBookingWidgetTitle = styled(Title).withConfig({ componentId: 'sc-18v994t-4' })`
  color: ${colors.myRightFoot};
`;

const Text = styled.div.withConfig({ componentId: 'sc-18v994t-5' })`
  color: ${colors.blueMonday};
  font-family: ${fontFamily.book};
  font-size: ${fontSize.mediumSmall};
  letter-spacing: -0.25px;
  line-height: 20px;
  text-align: center;
`;

const Container = styled.div.withConfig({ componentId: 'sc-18v994t-6' })`
  animation: ${FadeIn} 300ms linear;
  padding-left: 5%;
  padding-right: 5%;
`;

const ButtonContainer = styled.div.withConfig({ componentId: 'sc-18v994t-7' })`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  margin-top: 20px;
`;

const NextAvailableButton = styled(Button).withConfig({ componentId: 'sc-18v994t-8' })`
  padding: 0;
  height: 45px;
`;

const Copy = styled.span.withConfig({ componentId: 'sc-18v994t-9' })`
  width: 70%;
  margin: 5px 0;
  font-size: ${fontSize.mediumSmall};
  ${NextAvailableButton} & {
    white-space: pre-wrap;
  }
`;

type OwnProps = {
  selectedDate?: any;
  nextDate?: any;
  location?: any;
  nextDateSlots?: any[];
  selectedSlots?: any[];
  shouldShowWalkInCopy?: boolean;
  setAndHandleDate?: (...args: any[]) => any;
  handleClickShowMore?: (...args: any[]) => any;
  isBookingWidget?: boolean;
  t: TFunction<'common'>;
  // Below props are for old booking flow
  shouldShowNextAvailableAppointmentCta: boolean;
};

type Props = OwnProps & typeof ClinicClosed.defaultProps;

class ClinicClosed extends PureComponent<Props> {
  static defaultProps = {
    shouldShowWalkInCopy: true,
    // below props are for old booking flow
    shouldShowNextAvailableAppointmentCta: false,
  };

  shouldShowRecommendation = () =>
    // Only show second part of the copy if tomorrow (nextDate) has open hours and
    // has available slots
    this.props.shouldShowWalkInCopy &&
    dateHasOpenHours(this.props.location, this.props.nextDate) &&
    hasSlotsAvailable(this.props.nextDateSlots);

  handleFindNextAvailableVisit = () => {
    this.props.setAndHandleDate?.({ isMoreDate: true });

    this.props.handleClickShowMore?.();
  };

  render() {
    const {
      selectedDate,
      nextDate,
      location: { timezone },
      isBookingWidget,
      // below props are for old booking flow
      shouldShowNextAvailableAppointmentCta,
    } = this.props;

    const { t } = this.props;

    const day = getRelativeDayName(selectedDate, timezone);
    const nextDay = getRelativeDayName(nextDate, timezone);
    const getNextButtonVariation = () =>
      isBookingWidget ? ButtonVariations.pinkPrimary : ButtonVariations.pinkTertiary;

    const clinicClosedText = t('bookingTimeSelector.slotSelectorStates.clinicClosed.clinicClosed');

    return (
      <Container>
        <TitleContainer>
          {isBookingWidget ? (
            <>
              <StyledBookingWidgetAlert />
              <StyledBookingWidgetTitle>{clinicClosedText}</StyledBookingWidgetTitle>
            </>
          ) : (
            <>
              <StyledAlert />
              <Title>{clinicClosedText}</Title>
            </>
          )}
        </TitleContainer>

        <Text>
          {getClinicClosedXDay(day, t)}
          {this.shouldShowRecommendation() && (
            <>
              <br />
              {getYouCanStillBookXDay(nextDay, t)}
            </>
          )}
        </Text>
        {shouldShowNextAvailableAppointmentCta && (
          <ButtonContainer onClick={this.handleFindNextAvailableVisit}>
            <NextAvailableButton variation={getNextButtonVariation()} width="90%">
              <Copy>
                {t('bookingTimeSelector.slotSelectorStates.clinicClosed.findNextAvailableVisit')}
              </Copy>

              <ArrowForward />
            </NextAvailableButton>
          </ButtonContainer>
        )}
      </Container>
    );
  }
}

export default withTranslation()(ClinicClosed);
